// src/components/categories/CategoriesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetCategories from "../../hooks/categories/useGetCategories";
import useDeleteCategory from "../../hooks/categories/useDeleteCategory";
import useDeleteAllCategories from "../../hooks/categories/useDeleteAllCategories";
import useRestoreCategory from "../../hooks/categories/useRestoreCategory";
import useRestoreAllCategories from "../../hooks/categories/useRestoreAllCategories";
import useForceDeleteCategory from "../../hooks/categories/useForceDeleteCategory";
import useForceDeleteAllCategories from "../../hooks/categories/useForceDeleteAllCategories";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import CategoriesTableRow from "./Table/CategoriesTableRow";
import CategoriesTableColumns from "./Table/CategoriesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const CategoriesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("categories_list"),
            create: has("categories_create"),
            edit: has("categories_edit"),
            delete: has("categories_delete"),
            forceDelete: has("categories_force_delete"),
            archiveList: has("categories_archive_list"),
            restore: has("categories_restore"),
            restoreAll: has("categories_restore-all-table"),
            deleteAll: has("categories_delete-all-table"),
            massForceDeleteAll: has("categories_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/categories/archive");

    // CRUD Hooks
    const { deleteCat, isDelete } = useDeleteCategory();
    const { deleteAllCat, isDeleteAll } = useDeleteAllCategories();
    const { restoreCat, isRestore } = useRestoreCategory();
    const { restoreAllCat, isRestoreAll } = useRestoreAllCategories();
    const { forceDeleteCat, isForceDelete } = useForceDeleteCategory();
    const { forceDeleteAllCat, isForceDeleteAll } = useForceDeleteAllCategories();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/categories",
        deleteSingle: deleteCat,
        deleteAll: deleteAllCat,
        restoreSingle: restoreCat,
        restoreAll: restoreAllCat,
        forceDeleteSingle: forceDeleteCat,
        forceDeleteAll: forceDeleteAllCat,
        isArchive,
    });

    const filters = useMemo(
        () => ({
            search: search || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, sortBy, sortDir, isArchive]
    );

    const { isLoading, error, categories, meta } = useGetCategories(page, perPage, filters);

    const items = useMemo(() => categories?.items || [], [categories]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <CategoriesTableRow
            key={params.item.id}
            category={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "التصنيفات" : "Categories";
    const subtitle = isArchive
        ? isArabic
            ? "عرض التصنيفات المحذوفة (الأرشيف)"
            : "Archived categories (soft deleted)"
        : isArabic
            ? "إدارة التصنيفات المتاحة في النظام"
            : "Manage available categories in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة تصنيف"
            : "Create new category";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllCat({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/categories/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/categories/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/categories"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={CategoriesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد تصنيفات في الأرشيف" : "لا توجد تصنيفات"}
                emptyLabelEn={isArchive ? "No archived categories" : "No categories"}
                loadErrorAr="حدث خطأ أثناء تحميل التصنيفات"
                loadErrorEn="Failed to load categories"
            />
        </>
    );
};

export default CategoriesTable;
